import { useState } from "react";
import Api from "../api/api";
import Enums from "../../utilities/enum";
import { DateRangePicker } from "./dateRangePicker";
import { SearchSelect } from "./searchSelect";
import stringHelpers from "../../utilities/stringHelpers";
import { SearchFiltersModal } from "./searchFiltersModal";
import { toDateString } from "../../utilities/dateUtilities";
import { reportDatePresetByLabel } from "../reports/reportsEnums";
import { TagSelector } from "./tagSelector";
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities";
import arrayUtilities from "../../utilities/arrayUtilities";

export const AllowListFiltersModal = ({
	showModal,
	handleClose,
	handleConfirm,
	initialFilterStart,
	initialFilterEnd,
	initialDatePreset = null,
	initialFilterPeriodType,
	initialSiteId,
	initialClientId,
	initialSitesByClient,
	initialCreatedByUserId,
	initialCreatedBySource,
	initialSelectedTags = [],
	initialGlobalOnly,
	initialAppliedFilters,
	showSiteFilter = false,
	showClientsFilter = false,
	showTagsFilter = false,
	showGlobalFilter = false,
	sites = [],
	clients = [],
	createdByOptions = [],
	tags = [],
	organisationId,
	token
}) => {
	const [filterStart, setFilterStart] = useState(initialFilterStart)
	const [filterEnd, setFilterEnd] = useState(initialFilterEnd)
	const [datePreset, setDatePreset] = useState(initialDatePreset)
	const [filterPeriodType, setFilterPeriodType] = useState(initialFilterPeriodType)
	const [siteId, setSiteId] = useState(initialSiteId)
	const [clientId, setClientId] = useState(initialClientId)
	const [sitesByClient, setSitesByClient] = useState(initialSitesByClient ?? sites)
	const [createdByUserId, setCreatedByUserId] = useState(initialCreatedByUserId)
	const [createdBySource, setCreatedBySource] = useState(initialCreatedBySource)
	const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
	const [globalOnly, setGlobalOnly] = useState(initialGlobalOnly)
	const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

	const getCreatedByOptionValue = item => !stringHelpers.isNullOrEmpty(item.userId) ? item.userId : item.name

	const clearLocalFilters = () => {
		setFilterStart(null)
		setFilterEnd(null)
		setDatePreset(null)
		setFilterPeriodType("0")
		setSiteId(null)
		setClientId(null)
		setSitesByClient(sites)
		setCreatedByUserId(null)
		setCreatedBySource(null)
		setSelectedTags([])
		setGlobalOnly(false)
		setAppliedFilters(null)
	}

	const handleTagsChange = tagSelections => {
		setSelectedTags(tagSelections)
		setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
	}

	return (
		<SearchFiltersModal
			title="Filter Results"
			handleClose={ handleClose }
			handleConfirm={ () => handleConfirm({
				filterStart,
				filterEnd,
				datePreset,
				filterPeriodType,
				siteId: showSiteFilter ? siteId : undefined,
				clientId: showClientsFilter ? clientId : undefined,
				sitesByClient: showClientsFilter ? sitesByClient : undefined,
				createdByUserId,
				createdBySource,
				selectedTags: showTagsFilter ? selectedTags : undefined,
				globalOnly,
				appliedFilters
			}) }
			showModal={ showModal }
			key="allow-list-filters-modal"
			handleReset={ clearLocalFilters }
		>
			<div className="w-100">
				<div className="mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
					<DateRangePicker
						fromDate={ filterStart }
						toDate={ filterEnd }
						setDateRange={ (firstDate, lastDate, presetLabel) => {
							const isFirstDateEmpty = firstDate === null || firstDate === ""
							const isLastDateEmpty = lastDate === null || lastDate === ""
							setFilterStart(isFirstDateEmpty ? null : firstDate);
							setFilterEnd(isLastDateEmpty ? null : lastDate);
							setDatePreset(stringHelpers.isNullOrEmpty(presetLabel) ? null : (reportDatePresetByLabel[presetLabel] ?? null))
							if (!stringHelpers.isNullOrEmpty(presetLabel)) {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters.From
									delete updatedFilters.To
									updatedFilters["Date Range"] = presetLabel
									return updatedFilters
								})
							}
							else {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									if (!isFirstDateEmpty && !isLastDateEmpty) {
										delete updatedFilters.From
										delete updatedFilters.To
										updatedFilters["Date Range"] = `${toDateString(new Date(firstDate))} - ${toDateString(new Date(lastDate))}`
									} else {
										if (!isFirstDateEmpty) {
											updatedFilters.From = toDateString(new Date(firstDate))
										} else {
											delete updatedFilters.From
										}
										if (!isLastDateEmpty) {
											updatedFilters.To = toDateString(new Date(lastDate))
										} else {
											delete updatedFilters.To
										}
										delete updatedFilters["Date Range"]
									}
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							}
						}}
						showClearOption
						hideLabel
						fullWidth
						dropdownPositionRight
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Filter by Period Type</label>
					<SearchSelect
						defaultLabel="All"
						width="100%"
						options={ Object.entries(Enums.FilterPeriodType).map(([key, value]) => ({ value: key, label: value })) }
						onSelect={ value => {
							setFilterPeriodType(value)
							setAppliedFilters(prevState => {
								const updatedFilters = { ...prevState }
								if (value === "0") {
									delete updatedFilters["Period Type"]
								} else {
									updatedFilters["Period Type"] = Enums.FilterPeriodType[value]
								}
								return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
							})
						} }
						selected={ filterPeriodType }
					/>
				</div>

				{
					showGlobalFilter &&
					<div className="d-flex flex-column mb-2">

					<label className="m-0 mr-2 nowrap">Global Only</label>
					<small>Allow list entries that apply to all clients and all sites</small>
					<div className="custom-control custom-switch">
						<input className="custom-control-input" type="checkbox" id="global-only-toggle" checked={ globalOnly }
							onChange={ e => {
								setGlobalOnly(e.target.checked)
								if (e.target.checked) {
									setClientId(null)
									setSitesByClient(sites)
									setSiteId(null)
									setSelectedTags([])
								}
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									if (!e.target.checked) {
										delete updatedFilters["Global Only"]
									} else {
										updatedFilters["Global Only"] = "On"
										delete updatedFilters["Client"]
										delete updatedFilters["Site"]
										delete updatedFilters["Tags"]
									}
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							} }
						/>
						<label className="custom-control-label custom-toggle" htmlFor="global-only-toggle"></label>
					</div>
				</div>
				}

				{
					showClientsFilter && !globalOnly &&
					<div className="d-flex flex-column mb-2">
						<label className="m-0 mr-2 mb-1 nowrap">Client</label>
						<SearchSelect
							defaultLabel="All"
							width="100%"
							options={ [{ value: "", label: "All" }, ...clients.map(item => ({ value: item.id, label: item.name }))] }
							onSelect={ value => {
								if (stringHelpers.isNullOrEmpty(value)) {
									setClientId(null)
									setSitesByClient(sites)
									setSiteId(null)
									setAppliedFilters(prevState => {
										const updatedFilters = { ...prevState }
										delete updatedFilters["Client"]
										delete updatedFilters["Site"]
										return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
									})
								} else {
									setClientId(value)
									Api.getClientSites(
										data => {
											setSitesByClient(data)
											setSiteId(null)
										},
										error => console.log(error),
										value,
										organisationId,
										token
									)
									const selectedClient = clients.find(item => item.id === value)
									setAppliedFilters(prevState => {
										const updatedFilters = { ...prevState, "Client": selectedClient?.name }
										delete updatedFilters["Site"]
										return updatedFilters
									})
								}
							} }
							selected={ !stringHelpers.isNullOrEmpty(clientId) ? clientId : null }
						/>
					</div>
				}

				{
					showSiteFilter && !globalOnly &&
					<div className="d-flex flex-column mb-2">
						<label className="m-0 mr-2 mb-1 nowrap">Site</label>
						<SearchSelect
							defaultLabel="All"
							width="100%"
							options={ [{ value: "", label: "All" }, ...sitesByClient.map(item => ({ value: item.id, label: item.name }))] }
							onSelect={ value => {
								if (!stringHelpers.isNullOrEmpty(value)) {
									setSiteId(value)
									const selectedSite = sitesByClient.find(item => item.id === value)
									setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSite.name }))
								} else {
									setSiteId(null)
									setAppliedFilters(prevState => {
										const updatedFilters = { ...prevState }
										delete updatedFilters["Site"]
										return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
									})
								}
							} }
							selected={ !stringHelpers.isNullOrEmpty(siteId) ? siteId : null }
						/>
					</div>
				}

				{
					showTagsFilter && !globalOnly &&
					!arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
						<div className="d-flex align-items-center justify-content-between">
							<label className="m-0 mr-2 mb-1 nowrap">Tags</label>
							<button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
						</div>
						<TagSelector
							onChange={ handleTagsChange }
							selectedTags={ selectedTags }
							tags={ tags }
						/>
					</div> 
				}

				<div className="d-flex flex-column mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Created By</label>
					<SearchSelect
						defaultLabel="All"
						width="100%"
						options={
							[
								{ value: "", label: "All" },
								...createdByOptions.map(item => ({ value: getCreatedByOptionValue(item), label: item.name }))
							]
						}
						onSelect={ value => {
							if (stringHelpers.isNullOrEmpty(value)) {
								setCreatedByUserId(null)
								setCreatedBySource(null)
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters["Created By"]
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							} else {
								const selectedOption = createdByOptions.find(item => getCreatedByOptionValue(item) === value)
								if (stringHelpers.isNullOrEmpty(selectedOption.userId)) {
									setCreatedBySource(selectedOption.name)
									setCreatedByUserId(null)
								} else {
									setCreatedByUserId(selectedOption.userId)
									setCreatedBySource(null)
								}
								setAppliedFilters(prevState => ({ ...prevState, "Created By": selectedOption.name }))
							}
						} }
						selected={ !stringHelpers.isNullOrEmpty(createdByUserId) ? createdByUserId : (!stringHelpers.isNullOrEmpty(createdBySource) ? createdBySource : null) }
					/>
				</div>
			</div>
		</SearchFiltersModal>
	)
}
